import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import './voice.css';
import Voice from './Voice';
import CartesiaVoice from './CartesiaVoice';
import Header from '../../Components/Header/Header';
import ConfirmationBox from '../../Components/Confirmation/ConfirmationBox';
import { useAuthContext } from '../../hooks/UseAuthContext';
import { toast } from 'react-toastify';
import api from '../../api';
import Loader from '../../Components/Loader/Loader';
import GetChannel from '../Channels/GetChannel';

const VOICES_PER_PAGE   = 20;
const CARTESIA_PER_PAGE = 20;
const MEDIA_BASE        = 'https://api.alchemymix.com/channel_media';

function Voices() {
  const { user } = useAuthContext();

  // ── Step ───────────────────────────────────────────────────
  const [step, setStep]                         = useState(1);
  const [selectedChannel, setSelectedChannel]   = useState(null);
  const [channelPickerSel, setChannelPickerSel] = useState(null);

  // ── Provider tab ───────────────────────────────────────────
  const [activeProvider, setActiveProvider] = useState('speechify');

  // ── Speechify voices ───────────────────────────────────────
  const [allVoices, setAllVoices]         = useState([]);
  const [currentPage, setCurrentPage]     = useState(1);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voicesFetched, setVoicesFetched] = useState(false);
  const [activeVoiceId, setActiveVoiceId] = useState(null);

  // ── Speechify filters ──────────────────────────────────────
  const [genderFilter, setGenderFilter] = useState('all');
  const [typeFilter, setTypeFilter]     = useState('all');
  const [search, setSearch]             = useState('');

  // ── Speechify clone modal ──────────────────────────────────
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneForm, setCloneForm] = useState({
    name: '', gender: 'notSpecified', locale: 'en-US',
    consent_full_name: '', consent_email: '',
  });
  const [audioSample, setAudioSample]   = useState(null);
  const [isRecording, setIsRecording]   = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [cloning, setCloning]           = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);

  // ── Cartesia voices ────────────────────────────────────────
  const [cartesiaVoices, setCartesiaVoices]   = useState([]);
  const [cartesiaLoading, setCartesiaLoading] = useState(false);
  const [cartesiaFetched, setCartesiaFetched] = useState(false);
  const [cartesiaSearch, setCartesiaSearch]   = useState('');
  const [cartesiaGender, setCartesiaGender]   = useState('all');
  const [cartesiaLang, setCartesiaLang]       = useState('all');
  const [cartesiaPage, setCartesiaPage]       = useState(1);

  // ── Cartesia PIN gate ──────────────────────────────────────
  const [cartesiaPinVerified, setCartesiaPinVerified] = useState(false);
  const [showPinModal, setShowPinModal]               = useState(false);
  const [pinInput, setPinInput]                       = useState('');
  const [pinError, setPinError]                       = useState('');
  const [pinLoading, setPinLoading]                   = useState(false);

  // ── Cartesia clone modal ───────────────────────────────────
  const [showCartesiaCloneModal, setShowCartesiaCloneModal]     = useState(false);
  const [cartesiaCloneForm, setCartesiaCloneForm]               = useState({ name: '', description: '', language: 'en' });
  const [cartesiaCloneClip, setCartesiaCloneClip]               = useState(null);
  const [cartesiaCloneRecBlob, setCartesiaCloneRecBlob]         = useState(null);
  const [cartesiaCloneRecording, setCartesiaCloneRecording]     = useState(false);
  const [cartesiaCloning, setCartesiaCloning]                   = useState(false);

  const cartesiaRecorderRef = useRef(null);
  const cartesiaAudioRef    = useRef([]);

  // ── Confirmation ───────────────────────────────────────────
  const [pendingVoice, setPendingVoice]         = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [assigning, setAssigning]               = useState(false);

  // ── Fetch Speechify voices ─────────────────────────────────
  const fetchAllVoices = useCallback(async () => {
    if (voicesFetched) return;
    setLoadingVoices(true);
    try {
      const res = await api.get('/speechify/voices', {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setAllVoices(Array.isArray(res.data) ? res.data : []);
      setVoicesFetched(true);
    } catch {
      toast.error('Failed to load voices');
    } finally {
      setLoadingVoices(false);
    }
  }, [user, voicesFetched]);

  // ── Fetch Cartesia voices ──────────────────────────────────
  const fetchCartesiaVoices = useCallback(async () => {
    if (cartesiaFetched) return;
    setCartesiaLoading(true);
    try {
      const res = await api.get('/cartesia/voices', {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setCartesiaVoices(res.data?.data || []);
      setCartesiaFetched(true);
    } catch {
      toast.error('Failed to load Cartesia voices');
    } finally {
      setCartesiaLoading(false);
    }
  }, [user, cartesiaFetched]);

  // ── PIN submit ─────────────────────────────────────────────
  const handlePinSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!pinInput.trim()) return;
    setPinLoading(true);
    setPinError('');
    try {
      const res = await api.post('/cartesia/verify-pin',
        { pin: pinInput },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      if (res.data?.valid) {
        setCartesiaPinVerified(true);
        setShowPinModal(false);
        setPinInput('');
        fetchCartesiaVoices();
      } else {
        setPinError('Incorrect PIN. Please try again.');
      }
    } catch {
      setPinError('Something went wrong. Try again.');
    } finally {
      setPinLoading(false);
    }
  }, [pinInput, user, fetchCartesiaVoices]);

  const closePinModal = useCallback(() => {
    setShowPinModal(false);
    setActiveProvider('speechify');
    setPinInput('');
    setPinError('');
  }, []);

  // ── Cartesia tab click ─────────────────────────────────────
  const handleCartesiaTab = useCallback(() => {
    setActiveProvider('cartesia');
    if (!cartesiaPinVerified) {
      setShowPinModal(true);
    } else if (!cartesiaFetched) {
      fetchCartesiaVoices();
    }
  }, [cartesiaPinVerified, cartesiaFetched, fetchCartesiaVoices]);

  // ── Step 1 → Step 2 ───────────────────────────────────────
  const handleContinueWithChannel = useCallback(() => {
    if (!channelPickerSel) return;
    setSelectedChannel(channelPickerSel);
    setActiveVoiceId(channelPickerSel.voice || null);
    setStep(2);
    fetchAllVoices();
  }, [channelPickerSel, fetchAllVoices]);

  const handleExplore = useCallback(() => {
    setSelectedChannel(null);
    setActiveVoiceId(null);
    setStep(2);
    fetchAllVoices();
  }, [fetchAllVoices]);

  const handleBack = useCallback(() => {
    setStep(1);
    setSearch('');
    setGenderFilter('all');
    setTypeFilter('all');
    setCurrentPage(1);
    setActiveProvider('speechify');
    setCartesiaSearch('');
    setCartesiaGender('all');
    setCartesiaLang('all');
    setCartesiaPage(1);
  }, []);

  // ── Speechify filtering ────────────────────────────────────
  const filteredVoices = useMemo(() => {
    let r = allVoices;
    if (genderFilter !== 'all') r = r.filter(v => v.gender?.toLowerCase() === genderFilter);
    if (typeFilter !== 'all')   r = r.filter(v => v.type === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(v =>
        v.display_name?.toLowerCase().includes(q) ||
        v.locale?.toLowerCase().includes(q) ||
        v.gender?.toLowerCase().includes(q) ||
        v.tags?.some(t => t.toLowerCase().includes(q))
      );
    }
    return r;
  }, [allVoices, genderFilter, typeFilter, search]);

  const voices      = useMemo(() => filteredVoices.slice((currentPage - 1) * VOICES_PER_PAGE, currentPage * VOICES_PER_PAGE), [filteredVoices, currentPage]);
  const totalVoices = filteredVoices.length;
  const totalPages  = Math.ceil(totalVoices / VOICES_PER_PAGE);
  const clonedCount = allVoices.filter(v => v.type === 'personal').length;

  const activeVoiceName = useMemo(() =>
    allVoices.find(v => v.id === activeVoiceId)?.display_name ||
    cartesiaVoices.find(v => v.id === activeVoiceId)?.name,
  [allVoices, cartesiaVoices, activeVoiceId]);

  useEffect(() => { setCurrentPage(1); }, [genderFilter, typeFilter, search]);

  // ── Cartesia filtering + pagination ───────────────────────
  const cartesiaLanguages = useMemo(() =>
    [...new Set(cartesiaVoices.map(v => v.language).filter(Boolean))].sort(),
  [cartesiaVoices]);

  const filteredCartesiaVoices = useMemo(() => {
    let r = cartesiaVoices;
    if (cartesiaGender !== 'all') r = r.filter(v => v.gender?.toLowerCase() === cartesiaGender);
    if (cartesiaLang !== 'all')   r = r.filter(v => v.language === cartesiaLang);
    if (cartesiaSearch.trim()) {
      const q = cartesiaSearch.toLowerCase();
      r = r.filter(v =>
        v.name?.toLowerCase().includes(q) ||
        v.description?.toLowerCase().includes(q) ||
        v.language?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [cartesiaVoices, cartesiaGender, cartesiaLang, cartesiaSearch]);

  const cartesiaVoicesPage = useMemo(() =>
    filteredCartesiaVoices.slice(
      (cartesiaPage - 1) * CARTESIA_PER_PAGE,
      cartesiaPage * CARTESIA_PER_PAGE
    ),
  [filteredCartesiaVoices, cartesiaPage]);

  const cartesiaTotalPages = Math.ceil(filteredCartesiaVoices.length / CARTESIA_PER_PAGE);
  const cartesiaStartIdx   = (cartesiaPage - 1) * CARTESIA_PER_PAGE + 1;
  const cartesiaEndIdx     = Math.min(cartesiaPage * CARTESIA_PER_PAGE, filteredCartesiaVoices.length);

  useEffect(() => { setCartesiaPage(1); }, [cartesiaSearch, cartesiaGender, cartesiaLang]);

  // ── Voice assignment ───────────────────────────────────────
  const handleVoiceClick = useCallback((voice) => {
    if (!selectedChannel) { toast.error('Go back and select a channel first'); return; }
    if (activeVoiceId === voice.id) return;
    setPendingVoice(voice);
    setShowConfirmation(true);
  }, [selectedChannel, activeVoiceId]);

  const handleConfirm = useCallback(async (confirmed) => {
    setShowConfirmation(false);
    if (!confirmed || !pendingVoice || !selectedChannel) { setPendingVoice(null); return; }
    setAssigning(true);
    try {
      await api.post('/channel/update-audio',
        { channelId: selectedChannel._id, voice: pendingVoice.id },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setActiveVoiceId(pendingVoice.id);
      setSelectedChannel(prev => ({ ...prev, voice: pendingVoice.id }));
      const name = pendingVoice.display_name || pendingVoice.name;
      toast.success(`"${name}" assigned to ${selectedChannel.channelName}`);
      setPendingVoice(null);
    } catch { toast.error('Failed to assign voice'); }
    finally { setAssigning(false); }
  }, [pendingVoice, selectedChannel, user]);

  // ── Speechify pagination ───────────────────────────────────
  const handlePageChange = useCallback((p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [totalPages]);

  // ── Cartesia pagination ────────────────────────────────────
  const handleCartesiaPageChange = useCallback((p) => {
    if (p < 1 || p > cartesiaTotalPages) return;
    setCartesiaPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [cartesiaTotalPages]);

  // ── Speechify clone ────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => { setRecordedBlob(new Blob(audioChunksRef.current, { type: 'audio/webm' })); stream.getTracks().forEach(t => t.stop()); };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch { toast.error('Microphone access denied'); }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); }
  }, [isRecording]);

  const handleCloneSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!cloneForm.name.trim()) { toast.error('Voice name required'); return; }
    if (!cloneForm.consent_full_name.trim() || !cloneForm.consent_email.trim()) { toast.error('Consent info required'); return; }
    const finalAudio = recordedBlob ? new File([recordedBlob], 'recording.webm', { type: 'audio/webm' }) : audioSample;
    if (!finalAudio) { toast.error('Upload or record an audio sample'); return; }

    const fd = new FormData();
    fd.append('name', cloneForm.name.trim());
    fd.append('gender', cloneForm.gender);
    fd.append('locale', cloneForm.locale);
    fd.append('consent_full_name', cloneForm.consent_full_name.trim());
    fd.append('consent_email', cloneForm.consent_email.trim());
    fd.append('sample', finalAudio);

    setCloning(true);
    try {
      const res = await api.post('/speechify/voices/clone', fd, {
        headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.id) {
        toast.success(`"${res.data.display_name}" cloned!`);
        setVoicesFetched(false);
        setTimeout(() => fetchAllVoices(), 100);
        setShowCloneModal(false);
        setCloneForm({ name: '', gender: 'notSpecified', locale: 'en-US', consent_full_name: '', consent_email: '' });
        setAudioSample(null); setRecordedBlob(null);
      } else { toast.error(res.data?.error || 'Clone failed'); }
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to clone'); }
    finally { setCloning(false); }
  }, [cloneForm, audioSample, recordedBlob, user, fetchAllVoices]);

  // ── Cartesia clone ─────────────────────────────────────────
  const startCartesiaRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      cartesiaAudioRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) cartesiaAudioRef.current.push(e.data); };
      recorder.onstop = () => {
        setCartesiaCloneRecBlob(new Blob(cartesiaAudioRef.current, { type: 'audio/webm' }));
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      cartesiaRecorderRef.current = recorder;
      setCartesiaCloneRecording(true);
    } catch { toast.error('Microphone access denied'); }
  }, []);

  const stopCartesiaRecording = useCallback(() => {
    if (cartesiaRecorderRef.current && cartesiaCloneRecording) {
      cartesiaRecorderRef.current.stop();
      setCartesiaCloneRecording(false);
    }
  }, [cartesiaCloneRecording]);

  const handleCartesiaCloneSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!cartesiaCloneForm.name.trim()) { toast.error('Voice name required'); return; }
    const clip = cartesiaCloneRecBlob
      ? new File([cartesiaCloneRecBlob], 'recording.webm', { type: 'audio/webm' })
      : cartesiaCloneClip;
    if (!clip) { toast.error('Upload or record an audio clip (~5 seconds)'); return; }

    const fd = new FormData();
    fd.append('name', cartesiaCloneForm.name.trim());
    fd.append('language', cartesiaCloneForm.language);
    if (cartesiaCloneForm.description.trim()) fd.append('description', cartesiaCloneForm.description.trim());
    fd.append('clip', clip);

    setCartesiaCloning(true);
    try {
      const res = await api.post('/cartesia/voices/clone', fd, {
        headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.id) {
        toast.success(`"${res.data.name}" cloned!`);
        setCartesiaFetched(false);
        setTimeout(() => fetchCartesiaVoices(), 100);
        setShowCartesiaCloneModal(false);
        setCartesiaCloneForm({ name: '', description: '', language: 'en' });
        setCartesiaCloneClip(null);
        setCartesiaCloneRecBlob(null);
      } else { toast.error(res.data?.error || 'Clone failed'); }
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to clone voice'); }
    finally { setCartesiaCloning(false); }
  }, [cartesiaCloneForm, cartesiaCloneClip, cartesiaCloneRecBlob, user, fetchCartesiaVoices]);

  const startIdx = (currentPage - 1) * VOICES_PER_PAGE + 1;
  const endIdx   = Math.min(currentPage * VOICES_PER_PAGE, totalVoices);

  // ════════════════════════════════════════════════════════════
  // STEP 1
  // ════════════════════════════════════════════════════════════
  if (step === 1) {
    return (
      <div className="voices-container">
        <Header />
        <div className="vs-step1-page">
          <div className="vs-step1-content">

            <div className="vs-stepper">
              <div className="vs-stepper-item vs-stepper-item--active">
                <span className="vs-stepper-num">1</span>
                <span>Choose Channel</span>
              </div>
              <div className="vs-stepper-line" />
              <div className="vs-stepper-item">
                <span className="vs-stepper-num">2</span>
                <span>Pick Voice</span>
              </div>
            </div>

            <h1 className="vs-step1-title">Which channel are you<br />setting up?</h1>
            <p className="vs-step1-sub">Select a channel to assign it a voice. Or just explore voices without committing.</p>

            <div className="vs-channel-picker-wrap">
              <GetChannel setChannelData={setChannelPickerSel} />
            </div>

            <div className="vs-step1-actions">
              <button
                className="vs-btn-primary"
                disabled={!channelPickerSel}
                onClick={handleContinueWithChannel}
              >
                {channelPickerSel
                  ? <>Continue with <strong>{channelPickerSel.channelName}</strong> →</>
                  : 'Select a channel to continue'}
              </button>
              <button className="vs-btn-ghost" onClick={handleExplore}>
                Explore voices without selecting →
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // STEP 2
  // ════════════════════════════════════════════════════════════
  return (
    <div className="voices-container">
      <Header />
      <div className="voices">

        {/* ── Top bar ───────────────────────────────────── */}
        <div className="vs-topbar">
          <button className="vs-back-btn" onClick={handleBack}>← Back</button>

          <div className="vs-stepper vs-stepper--inline">
            <div className="vs-stepper-item vs-stepper-item--done">
              <span className="vs-stepper-num">✓</span>
              <span>{selectedChannel ? selectedChannel.channelName : 'Exploring'}</span>
            </div>
            <div className="vs-stepper-line" />
            <div className="vs-stepper-item vs-stepper-item--active">
              <span className="vs-stepper-num">2</span>
              <span>Pick Voice</span>
            </div>
          </div>

          {activeProvider === 'speechify' && (
            <button className="vs-clone-btn" onClick={() => setShowCloneModal(true)}>
              + Clone Voice
            </button>
          )}
          {activeProvider === 'cartesia' && cartesiaPinVerified && (
            <button className="vs-clone-btn" onClick={() => setShowCartesiaCloneModal(true)}>
              + Clone Voice
            </button>
          )}
        </div>

        {/* ── Provider tabs ─────────────────────────────── */}
        <div className="vs-provider-tabs">
          <button
            className={`vs-provider-tab ${activeProvider === 'speechify' ? 'vs-provider-tab--active' : ''}`}
            onClick={() => setActiveProvider('speechify')}
          >
            Speechify
          </button>
          <button
            className={`vs-provider-tab ${activeProvider === 'cartesia' ? 'vs-provider-tab--active' : ''}`}
            onClick={handleCartesiaTab}
          >
            ✦ Cartesia
            <span className="vs-premium-badge">Premium</span>
          </button>
        </div>

        {/* ── Channel context strip ──────────────────────── */}
        {selectedChannel ? (
          <div className="vs-channel-strip">
            <img
              src={`${MEDIA_BASE}/${selectedChannel.logo}`}
              alt={selectedChannel.channelName}
              className="vs-strip-logo"
              onError={e => { e.target.style.display = 'none'; }}
            />
            <div>
              <span className="vs-strip-label">
                Assigning {activeProvider === 'cartesia' ? 'Cartesia' : 'Speechify'} voice to
              </span>
              <span className="vs-strip-name">{selectedChannel.channelName}</span>
            </div>
            {activeVoiceName && (
              <div className="vs-strip-current">
                <span className="vs-strip-dot" />
                Current: <strong>{activeVoiceName}</strong>
              </div>
            )}
          </div>
        ) : (
          <div className="vs-explore-strip">
            <span className="vs-explore-icon">
              {activeProvider === 'cartesia' ? '✦' : '🎧'}
            </span>
            <span>
              Exploring {activeProvider === 'cartesia' ? 'Cartesia' : 'Speechify'} voices —{' '}
              <button className="vs-explore-link" onClick={handleBack}>
                go back to select a channel
              </button>{' '}
              to assign one
            </span>
          </div>
        )}

        {/* ════════════════════════════════════════════════
            SPEECHIFY PANEL
        ════════════════════════════════════════════════ */}
        {activeProvider === 'speechify' && (
          <>
            <div className="voices-filters">
              <input
                className="voices-search"
                type="text"
                placeholder="Search by name, language, tag…"
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              />
              <div className="voices-filter-group">
                <label>Gender</label>
                <select value={genderFilter} onChange={e => { setGenderFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="voices-filter-group">
                <label>Type</label>
                <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All</option>
                  <option value="shared">Library</option>
                  <option value="personal">Cloned {clonedCount > 0 ? `(${clonedCount})` : ''}</option>
                </select>
              </div>
            </div>

            {!loadingVoices && (
              <p className="voices-count">
                Showing {startIdx}–{endIdx} of {totalVoices} voice{totalVoices !== 1 ? 's' : ''}
              </p>
            )}

            {loadingVoices ? <Loader /> : voices.length === 0 ? (
              <p className="voices-empty">No voices match your filters.</p>
            ) : (
              <div className="ai-voices">
                {voices.map(voice => {
                  const isActive = activeVoiceId === voice.id;
                  const isCloned = voice.type === 'personal';
                  return (
                    <div
                      key={voice.id}
                      className={`singles-voice ${isActive ? 'singles-voice--active' : ''} ${isCloned ? 'singles-voice--cloned' : ''}`}
                    >
                      <div className="voice-name">
                        <div className="voice-name-left">
                          <h3>{voice.display_name}</h3>
                          <div className="voice-meta">
                            {voice.locale && <span className="voice-tag voice-tag--locale">{voice.locale}</span>}
                            {voice.gender && voice.gender !== 'notSpecified' && <span className="voice-tag voice-tag--gender">{voice.gender}</span>}
                            {isCloned && <span className="voice-tag voice-tag--cloned">Cloned</span>}
                            {isActive && <span className="voice-tag voice-tag--active">Active</span>}
                          </div>
                        </div>
                        <div className="voice-name-right">
                          {selectedChannel ? (
                            <div className="checkbox-wrapper-31">
                              <input type="checkbox" checked={isActive} onChange={() => handleVoiceClick(voice)} aria-label={`Select ${voice.display_name}`} />
                              <svg viewBox="0 0 35.6 35.6">
                                <circle className="background" cx="17.8" cy="17.8" r="17.8" />
                                <circle className="stroke" cx="17.8" cy="17.8" r="14.37" />
                                <polyline className="check" points="11.78 18.12 15.55 22.23 25.17 12.87" />
                              </svg>
                            </div>
                          ) : (
                            <button className="vs-pick-channel-btn" onClick={handleBack} title="Go back to select a channel">Assign</button>
                          )}
                        </div>
                      </div>
                      <div className="voice-record">
                        <Voice voiceId={voice.id} previewUrl={voice.preview_audio} authToken={user.token} isCloned={isCloned} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loadingVoices && totalPages > 1 && (
              <div className="voices-pagination">
                <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>← Previous</button>
                <div className="pagination-pages">
                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= 7)                    page = i + 1;
                    else if (currentPage <= 4)              page = i + 1;
                    else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                    else                                    page = currentPage - 3 + i;
                    return (
                      <button key={page} className={`pagination-page ${currentPage === page ? 'active' : ''}`} onClick={() => handlePageChange(page)}>{page}</button>
                    );
                  })}
                </div>
                <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next →</button>
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════
            CARTESIA PANEL
        ════════════════════════════════════════════════ */}
        {activeProvider === 'cartesia' && cartesiaPinVerified && (
          <>
            <div className="voices-filters">
              <input
                className="voices-search"
                type="text"
                placeholder="Search Cartesia voices…"
                value={cartesiaSearch}
                onChange={e => setCartesiaSearch(e.target.value)}
              />
              <div className="voices-filter-group">
                <label>Gender</label>
                <select value={cartesiaGender} onChange={e => setCartesiaGender(e.target.value)}>
                  <option value="all">All</option>
                  <option value="masculine">Masculine</option>
                  <option value="feminine">Feminine</option>
                  <option value="gender_neutral">Neutral</option>
                </select>
              </div>
              {cartesiaLanguages.length > 0 && (
                <div className="voices-filter-group">
                  <label>Language</label>
                  <select value={cartesiaLang} onChange={e => setCartesiaLang(e.target.value)}>
                    <option value="all">All</option>
                    {cartesiaLanguages.map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {!cartesiaLoading && (
              <p className="voices-count">
                Showing {cartesiaStartIdx}–{cartesiaEndIdx} of {filteredCartesiaVoices.length} voice{filteredCartesiaVoices.length !== 1 ? 's' : ''}
              </p>
            )}

            {cartesiaLoading ? <Loader /> : cartesiaVoicesPage.length === 0 ? (
              <p className="voices-empty">No Cartesia voices found.</p>
            ) : (
              <div className="ai-voices">
                {cartesiaVoicesPage.map(voice => {
                  const isActive = activeVoiceId === voice.id;
                  return (
                    <div
                      key={voice.id}
                      className={`singles-voice singles-voice--cartesia ${isActive ? 'singles-voice--active' : ''}`}
                    >
                      <div className="voice-name">
                        <div className="voice-name-left">
                          <h3>{voice.name}</h3>
                          <div className="voice-meta">
                            {voice.language && <span className="voice-tag voice-tag--locale">{voice.language}</span>}
                            {voice.gender && <span className="voice-tag voice-tag--gender">{voice.gender}</span>}
                            {voice.is_owner && <span className="voice-tag voice-tag--cloned">Owned</span>}
                            <span className="voice-tag voice-tag--cartesia">Cartesia</span>
                            {isActive && <span className="voice-tag voice-tag--active">Active</span>}
                          </div>
                          {voice.description && (
                            <p className="vs-voice-desc">{voice.description}</p>
                          )}
                        </div>
                        <div className="voice-name-right">
                          {selectedChannel ? (
                            <div className="checkbox-wrapper-31">
                              <input type="checkbox" checked={isActive} onChange={() => handleVoiceClick(voice)} aria-label={`Select ${voice.name}`} />
                              <svg viewBox="0 0 35.6 35.6">
                                <circle className="background" cx="17.8" cy="17.8" r="17.8" />
                                <circle className="stroke" cx="17.8" cy="17.8" r="14.37" />
                                <polyline className="check" points="11.78 18.12 15.55 22.23 25.17 12.87" />
                              </svg>
                            </div>
                          ) : (
                            <button className="vs-pick-channel-btn" onClick={handleBack}>Assign</button>
                          )}
                        </div>
                      </div>
                      <div className="voice-record">
                        <CartesiaVoice
                          voiceId={voice.id}
                          previewUrl={voice.preview_file_url}
                          authToken={user.token}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!cartesiaLoading && cartesiaTotalPages > 1 && (
              <div className="voices-pagination">
                <button className="pagination-btn" onClick={() => handleCartesiaPageChange(cartesiaPage - 1)} disabled={cartesiaPage === 1}>← Previous</button>
                <div className="pagination-pages">
                  {Array.from({ length: Math.min(7, cartesiaTotalPages) }, (_, i) => {
                    let page;
                    if (cartesiaTotalPages <= 7)                     page = i + 1;
                    else if (cartesiaPage <= 4)                      page = i + 1;
                    else if (cartesiaPage >= cartesiaTotalPages - 3) page = cartesiaTotalPages - 6 + i;
                    else                                             page = cartesiaPage - 3 + i;
                    return (
                      <button key={page} className={`pagination-page ${cartesiaPage === page ? 'active' : ''}`} onClick={() => handleCartesiaPageChange(page)}>{page}</button>
                    );
                  })}
                </div>
                <button className="pagination-btn" onClick={() => handleCartesiaPageChange(cartesiaPage + 1)} disabled={cartesiaPage === cartesiaTotalPages}>Next →</button>
              </div>
            )}
          </>
        )}

      </div>

      {/* ── Speechify Clone Modal ─────────────────────────────── */}
      {showCloneModal && (
        <div className="clone-modal-overlay" onClick={() => setShowCloneModal(false)}>
          <div className="clone-modal" onClick={e => e.stopPropagation()}>
            <div className="clone-modal-header">
              <h3>Clone a Voice</h3>
              <button className="clone-modal-close" onClick={() => setShowCloneModal(false)}>×</button>
            </div>
            <form onSubmit={handleCloneSubmit} className="clone-modal-body">
              <div className="clone-row">
                <div className="clone-field">
                  <label>Voice Name *</label>
                  <input type="text" className="clone-input" placeholder="e.g. Channel Host" value={cloneForm.name} onChange={e => setCloneForm(p => ({ ...p, name: e.target.value }))} required />
                </div>
                <div className="clone-field">
                  <label>Gender *</label>
                  <select className="clone-input" value={cloneForm.gender} onChange={e => setCloneForm(p => ({ ...p, gender: e.target.value }))}>
                    <option value="notSpecified">Not Specified</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
              <div className="clone-field">
                <label>Language</label>
                <select className="clone-input" value={cloneForm.locale} onChange={e => setCloneForm(p => ({ ...p, locale: e.target.value }))}>
                  <option value="en-US">English (US)</option><option value="en-GB">English (UK)</option>
                  <option value="es-ES">Spanish (ES)</option><option value="es-MX">Spanish (MX)</option>
                  <option value="fr-FR">French</option><option value="de-DE">German</option>
                  <option value="it-IT">Italian</option><option value="pt-BR">Portuguese (BR)</option>
                  <option value="ja-JP">Japanese</option><option value="zh-CN">Chinese (Simplified)</option>
                </select>
              </div>
              <div className="clone-field">
                <label>Audio Sample * <span className="clone-hint">(10–30 sec, under 5MB)</span></label>
                <div className="clone-audio-row">
                  <label className={`clone-upload-btn ${audioSample ? 'clone-upload-btn--active' : ''}`}>
                    {audioSample ? `✓ ${audioSample.name}` : '📁 Upload File'}
                    <input type="file" accept="audio/*" hidden onChange={e => { setAudioSample(e.target.files[0]); setRecordedBlob(null); }} />
                  </label>
                  <span className="clone-or">or</span>
                  <button type="button" className={`clone-record-btn ${isRecording ? 'clone-record-btn--recording' : ''} ${recordedBlob ? 'clone-record-btn--done' : ''}`} onClick={isRecording ? stopRecording : startRecording}>
                    {isRecording ? '⏹ Stop' : recordedBlob ? '✓ Recorded' : '🎙 Record'}
                  </button>
                </div>
              </div>
              <div className="clone-consent-box">
                <p className="clone-consent-label">Consent — this voice belongs to you or someone you represent</p>
                <div className="clone-row">
                  <div className="clone-field">
                    <label>Full Name *</label>
                    <input type="text" className="clone-input" placeholder="John Doe" value={cloneForm.consent_full_name} onChange={e => setCloneForm(p => ({ ...p, consent_full_name: e.target.value }))} required />
                  </div>
                  <div className="clone-field">
                    <label>Email *</label>
                    <input type="email" className="clone-input" placeholder="john@example.com" value={cloneForm.consent_email} onChange={e => setCloneForm(p => ({ ...p, consent_email: e.target.value }))} required />
                  </div>
                </div>
              </div>
              <button type="submit" className="clone-submit-btn" disabled={cloning}>{cloning ? '⏳ Cloning…' : '✨ Clone Voice'}</button>
            </form>
          </div>
        </div>
      )}

      {/* ── Cartesia Clone Modal ──────────────────────────────── */}
      {showCartesiaCloneModal && (
        <div className="clone-modal-overlay" onClick={() => setShowCartesiaCloneModal(false)}>
          <div className="clone-modal" onClick={e => e.stopPropagation()}>
            <div className="clone-modal-header">
              <h3>✦ Clone a Cartesia Voice</h3>
              <button className="clone-modal-close" onClick={() => setShowCartesiaCloneModal(false)}>×</button>
            </div>
            <form onSubmit={handleCartesiaCloneSubmit} className="clone-modal-body">
              <div className="clone-row">
                <div className="clone-field">
                  <label>Voice Name *</label>
                  <input
                    type="text"
                    className="clone-input"
                    placeholder="e.g. My Voice"
                    value={cartesiaCloneForm.name}
                    onChange={e => setCartesiaCloneForm(p => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="clone-field">
                  <label>Language</label>
                  <select
                    className="clone-input"
                    value={cartesiaCloneForm.language}
                    onChange={e => setCartesiaCloneForm(p => ({ ...p, language: e.target.value }))}
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="es">Spanish</option>
                    <option value="pt">Portuguese</option>
                    <option value="zh">Chinese</option>
                    <option value="ja">Japanese</option>
                    <option value="hi">Hindi</option>
                    <option value="it">Italian</option>
                    <option value="ko">Korean</option>
                  </select>
                </div>
              </div>
              <div className="clone-field">
                <label>Description <span className="clone-hint">(optional)</span></label>
                <input
                  type="text"
                  className="clone-input"
                  placeholder="e.g. Calm male narrator"
                  value={cartesiaCloneForm.description}
                  onChange={e => setCartesiaCloneForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="clone-field">
                <label>Audio Clip * <span className="clone-hint">(~5 seconds works best)</span></label>
                <div className="clone-audio-row">
                  <label className={`clone-upload-btn ${cartesiaCloneClip ? 'clone-upload-btn--active' : ''}`}>
                    {cartesiaCloneClip ? `✓ ${cartesiaCloneClip.name}` : '📁 Upload File'}
                    <input
                      type="file"
                      accept="audio/*"
                      hidden
                      onChange={e => { setCartesiaCloneClip(e.target.files[0]); setCartesiaCloneRecBlob(null); }}
                    />
                  </label>
                  <span className="clone-or">or</span>
                  <button
                    type="button"
                    className={`clone-record-btn ${cartesiaCloneRecording ? 'clone-record-btn--recording' : ''} ${cartesiaCloneRecBlob ? 'clone-record-btn--done' : ''}`}
                    onClick={cartesiaCloneRecording ? stopCartesiaRecording : startCartesiaRecording}
                  >
                    {cartesiaCloneRecording ? '⏹ Stop' : cartesiaCloneRecBlob ? '✓ Recorded' : '🎙 Record'}
                  </button>
                </div>
              </div>
              <button type="submit" className="clone-submit-btn" disabled={cartesiaCloning}>
                {cartesiaCloning ? '⏳ Cloning…' : '✦ Clone Voice'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Cartesia PIN Modal ────────────────────────────────── */}
      {showPinModal && (
        <div className="clone-modal-overlay" onClick={closePinModal}>
          <div className="clone-modal vs-pin-modal" onClick={e => e.stopPropagation()}>
            <div className="clone-modal-header">
              <h3>✦ Cartesia Premium</h3>
              <button className="clone-modal-close" onClick={closePinModal}>×</button>
            </div>
            <form onSubmit={handlePinSubmit} className="clone-modal-body">
              <p className="vs-pin-desc">
                Cartesia is a premium voice provider with superior emotional intelligence and prosody.
                Enter your access PIN to unlock it.
              </p>
              <div className="clone-field">
                <label>Access PIN</label>
                <input
                  type="password"
                  className="clone-input vs-pin-input"
                  placeholder="Enter PIN…"
                  value={pinInput}
                  onChange={e => { setPinInput(e.target.value); setPinError(''); }}
                  autoFocus
                />
                {pinError && <p className="vs-pin-error">{pinError}</p>}
              </div>
              <button
                type="submit"
                className="clone-submit-btn"
                disabled={pinLoading || !pinInput.trim()}
              >
                {pinLoading ? '⏳ Verifying…' : 'Unlock Cartesia →'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirmation ──────────────────────────────────────── */}
      {showConfirmation && (
        <ConfirmationBox
          message={`Assign "${pendingVoice?.display_name || pendingVoice?.name}" as the voice for ${selectedChannel?.channelName}?`}
          handleConfirm={handleConfirm}
        />
      )}

      {assigning && <Loader />}
    </div>
  );
}

export default Voices;